import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import './CLI.css';

const CLI = ({ config }) => {
  const [history, setHistory] = useState([
    {
      type: 'system',
      content: 'hledger CLI Interface - Type commands WITHOUT "hledger" prefix!\n\nExamples: bs, balance, register expenses, accounts\n(Do NOT type: hledger bs)'
    }
  ]);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to bottom when history changes
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();
  }, []);

  const executeCommand = async (command) => {
    if (!command.trim()) return;

    // Add command to history
    setHistory(prev => [...prev, { type: 'command', content: command }]);
    setCommandHistory(prev => [...prev, command]);
    setHistoryIndex(-1);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(getApiUrl('/api/hledger/command'), {
        command: command,
        journalPath: config.journalPath
      });

      const output = response.data.stdout || response.data.stderr || 'Command executed successfully';
      setHistory(prev => [...prev, { type: 'output', content: output }]);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.stderr || error.message;
      setHistory(prev => [...prev, { type: 'error', content: errorMsg }]);
    }

    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    const trimmedInput = input.trim();

    // Handle special commands
    if (trimmedInput === 'clear') {
      setHistory([{
        type: 'system',
        content: 'hledger CLI Interface - Type commands WITHOUT "hledger" prefix!\n\nExamples: bs, balance, register expenses, accounts\n(Do NOT type: hledger bs)'
      }]);
      setInput('');
      return;
    }

    if (trimmedInput === 'help') {
      setHistory(prev => [...prev,
        { type: 'command', content: 'help' },
        {
          type: 'system',
          content: `Available commands:
  - Any hledger command WITHOUT "hledger" prefix
  - clear: Clear the terminal
  - help: Show this help message

Examples (type these exactly):
  bs                          (balance sheet)
  balance                     (NOT: hledger balance)
  register expenses -p lastmonth
  accounts Assets
  stats
  roi

IMPORTANT: Do NOT type "hledger" - just the command!`
        }
      ]);
      setInput('');
      return;
    }

    executeCommand(trimmedInput);
  };

  const handleKeyDown = (e) => {
    // Up arrow - previous command
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length === 0) return;

      const newIndex = historyIndex === -1
        ? commandHistory.length - 1
        : Math.max(0, historyIndex - 1);

      setHistoryIndex(newIndex);
      setInput(commandHistory[newIndex]);
    }

    // Down arrow - next command
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex === -1) return;

      const newIndex = historyIndex + 1;
      if (newIndex >= commandHistory.length) {
        setHistoryIndex(-1);
        setInput('');
      } else {
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    }

    // Ctrl+C - clear current input
    if (e.key === 'c' && e.ctrlKey) {
      e.preventDefault();
      setInput('');
    }

    // Ctrl+L - clear terminal
    if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setHistory([{
        type: 'system',
        content: 'hledger CLI Interface - Type commands WITHOUT "hledger" prefix!\n\nExamples: bs, balance, register expenses, accounts\n(Do NOT type: hledger bs)'
      }]);
    }
  };

  return (
    <div className="cli-page">
      <div className="page-header">
        <h1>CLI Interface</h1>
        <p>Execute hledger commands directly</p>
      </div>

      <div className="cli-container">
        <div className="cli-terminal" ref={terminalRef}>
          {history.map((entry, index) => (
            <div key={index} className={`cli-entry cli-entry-${entry.type}`}>
              {entry.type === 'command' && (
                <div className="cli-prompt">
                  <span className="cli-prompt-symbol">$</span>
                  <span className="cli-prompt-text">hledger {entry.content}</span>
                </div>
              )}
              {entry.type === 'output' && (
                <pre className="cli-output">{entry.content}</pre>
              )}
              {entry.type === 'error' && (
                <pre className="cli-error">{entry.content}</pre>
              )}
              {entry.type === 'system' && (
                <pre className="cli-system">{entry.content}</pre>
              )}
            </div>
          ))}
          {loading && (
            <div className="cli-loading">
              <div className="spinner"></div>
              <span>Executing command...</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="cli-input-form">
          <div className="cli-input-container">
            <span className="cli-input-prompt">$ hledger</span>
            <input
              ref={inputRef}
              type="text"
              className="cli-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              placeholder="bs, balance, register, help (NO 'hledger' prefix!)"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
        </form>

        <div className="cli-help-bar">
          <span>↑↓ Command history</span>
          <span>Ctrl+C Clear input</span>
          <span>Ctrl+L Clear terminal</span>
          <span>Type "help" for more info</span>
        </div>
      </div>
    </div>
  );
};

export default CLI;
